import { StaticCardTypes } from '@shared/components/cards/card-types';
import Card from '@shared/components/cards/default-cards';
import { RemoteSystems, UserRoles } from '../../../constants/admin/admin-lists.constants';
import { html } from '@shared/utils/rendering/html';
import CheckboxGroup from '@shared/components/forms/form-components/checkboxes/checkbox-group';
import TextInput from '@shared/components/forms/form-components/inputs/text-input';
import { wrapWithLayout } from '@shared/components/forms/form-components/utils/layout-wrapper';
import { legalEntities } from '../../../constants/utility/admin/legal-entities';
import UserLegalEntities from '../../../components/admin/users/user-details/user-legal-entities';
import UserHierarchyNodes from '../../../components/admin/users/user-details/user-hierarchy-nodes';

// These 4 fields are read-only display fields - omitting maxLength (vs. the default
// renderStringField behaviour) suppresses TextInput's "N characters remaining" helper text,
// which only ever makes sense while actively typing into an editable field.
const renderWithoutCharacterCount = (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
    const component = html`<${TextInput}
        ref=${fieldRef}
        ...${inputProps}
        disabled=${layoutSettings.disabled}
    />`;
    return { render: wrapWithLayout(component, layoutSettings), fieldRef };
};

export const UserDetailsFormLayout = {
    firstName: {
        column: 'col-3',
        formGroup: '',
        css: '',
        disabled: true,
    },
    lastName: {
        column: 'col-3',
        formGroup: '',
        css: '',
        disabled: true,
    },
    email: {
        column: 'col-3',
        formGroup: '',
        css: '',
        disabled: true,
    },
    jobTitle: {
        column: 'col-3',
        formGroup: '',
        css: '',
        disabled: true,
    },
    roles: {
        column: 'col-12',
        formGroup: '',
        css: '',
        disabled: true,
    },
    systems: {
        column: 'col-12',
        formGroup: '',
        css: '',
    },
    hierarchyNodes: {
        column: 'col-12',
        formGroup: '',
        css: '',
    },
};

export const UserDetailsFormOptions = {
    layout: UserDetailsFormLayout,
    includeSaveButton: false,
    formProps: {
        disabled: false,
    },
    options: {
        // Use this to populate "array" fields (dropdown options)
        roles: Object.values(UserRoles).map((role) => {
            return { value: role.id, text: role.name };
        }),
        systems: Object.values(RemoteSystems).map((system) => {
            return { value: system.systemId, text: system.systemName };
        }),
        options: [],
    },
    overrides: {
        firstName: renderWithoutCharacterCount,
        lastName: renderWithoutCharacterCount,
        email: renderWithoutCharacterCount,
        jobTitle: renderWithoutCharacterCount,
        systems: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
            const render = html`<${CheckboxGroup}
                inputProps=${inputProps}
                layoutSettings=${layoutSettings}
                options=${options}
                ref=${fieldRef}
            />`;
            return { render, fieldRef };
        },
        roles: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
            const render = html`<${CheckboxGroup}
                inputProps=${inputProps}
                layoutSettings=${layoutSettings}
                options=${options}
                ref=${fieldRef}
            />`;
            return { render, fieldRef };
        },
        legalEntities: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
            const render = html`<${UserLegalEntities}
                inputProps=${inputProps}
                layoutSettings=${layoutSettings}
                options=${options}
                ref=${fieldRef}
            />`;
            return { render, fieldRef };
        },
        hierarchyNodes: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
            const render = html`<${UserHierarchyNodes}
                inputProps=${inputProps}
                layoutSettings=${layoutSettings}
                options=${options}
                ref=${fieldRef}
            />`;
            return { render, fieldRef };
        },
    }, // Use this for custom render callbacks
    converters: {}, // Use this for converting dropdown options into chips
    sections: [
        {
            parent: Card,
            parentProps: {
                header: { title: 'Personal Details' },
                content: {
                    type: StaticCardTypes.other,
                },
                css: 'my-3',
            },
            fields: ['firstName', 'lastName', 'email', 'jobTitle'],
        },

        {
            parent: Card,
            parentProps: {
                header: { title: 'Access Roles' },
                content: {
                    type: StaticCardTypes.other,
                },
                css: 'my-3',
            },
            fields: ['roles'],
        },

        {
            parent: Card,
            parentProps: {
                header: { title: 'System Access' },
                content: {
                    type: StaticCardTypes.other,
                },
                css: 'my-3',
            },
            fields: ['systems'],
        },

        {
            parent: Card,
            parentProps: {
                header: { title: 'Legal Entity / Business Unit' },
                content: {
                    type: StaticCardTypes.other,
                },
                css: 'my-3',
            },
            fields: ['legalEntities'],
        },

        {
            parent: Card,
            parentProps: {
                header: { title: 'Hierarchy Nodes' },
                content: {
                    type: StaticCardTypes.other,
                },
                css: 'my-3',
            },
            fields: ['hierarchyNodes'],
        },
    ],
};
